import React from 'react';

/**
 * Side-by-side view of exactly what was compared for each scoring component
 * (person name, purpose, program, restriction) — the expenditure value, the
 * award value, the outcome and the score — so reviewers can verify a match.
 *
 * Expects the `match_details` object produced by the server for a matched award.
 */

const RESULT_LABEL = {
  exact: 'Exact',
  fuzzy: 'Fuzzy',
  mismatch: 'Mismatch',
  none: 'No match',
  highly: 'Highly restricted',
  moderately: 'Moderately restricted',
};

const RESULT_CLASS = {
  exact: 'high',
  fuzzy: 'mid',
  mismatch: 'low',
  none: 'low',
  highly: 'high',
  moderately: 'mid',
};

const SOURCE_LABEL = {
  ords: 'ORDS field',
  heuristic: 'text heuristic',
  llm: 'LLM',
};

function fmtScore(s) {
  if (s == null) return '-';
  const n = Number(s);
  return n > 0 ? `+${n}` : `${n}`;
}

function clip(v, max = 160) {
  const s = v == null ? '' : String(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default function MatchDetails({ details }) {
  if (!details) return null;
  const rows = [
    ['Person name', details.person_name],
    ['Purpose',     details.purpose],
    ['Program',     details.program],
    ['Restriction', details.restriction],
  ].filter(([, d]) => d);

  if (rows.length === 0) return null;

  return (
    <div className="match-details" style={{ marginTop: 8 }}>
      <div className="match-evidence-header">What Matched</div>
      <table className="match-details-table" style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
            <th style={{ padding: '2px 6px' }}>Component</th>
            <th style={{ padding: '2px 6px' }}>Expenditure</th>
            <th style={{ padding: '2px 6px' }}>Award</th>
            <th style={{ padding: '2px 6px' }}>Result</th>
            <th style={{ padding: '2px 6px', textAlign: 'right' }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, d]) => {
            const outcome = d.result || d.level || 'none';
            const cls = RESULT_CLASS[outcome] || 'low';
            return (
              <tr key={label} style={{ borderTop: '1px solid var(--border, #e5e7eb)', verticalAlign: 'top' }}>
                <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {label}
                  {d.source && (
                    <div style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 10 }}>
                      via {SOURCE_LABEL[d.source] || d.source}
                    </div>
                  )}
                </td>
                <td style={{ padding: '3px 6px' }} title={d.expenditure_value || ''}>
                  {d.expenditure_value ? clip(d.expenditure_value) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  {d.expenditure_field && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{d.expenditure_field}</div>
                  )}
                </td>
                <td style={{ padding: '3px 6px' }} title={d.award_value || ''}>
                  {d.award_value ? clip(d.award_value) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  {d.award_field && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{d.award_field}</div>
                  )}
                </td>
                <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>
                  <span className={`score-color-dot ${cls}`} style={{ marginRight: 4 }} />
                  {RESULT_LABEL[outcome] || outcome}
                  {d.tier1 && <span className="field-tag" style={{ marginLeft: 4 }}>Tier 1</span>}
                  {d.overlap != null && outcome !== 'none' && d.overlap > 0 && (
                    <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{Math.round(d.overlap * 100)}%</span>
                  )}
                </td>
                <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 700 }}>{fmtScore(d.score)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
